import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { StatusManager } from '../../components/dashboard/StatusManager';
import PhotoGalleryManager from '../../components/dashboard/PhotoGalleryManager';
import DesignManager from '../../components/dashboard/DesignManager';
import { useAuth } from '../../context/AuthContext';
import { 
  FiArrowLeft, FiUser, FiMail, FiPhone, FiMapPin, 
  FiCalendar, FiPackage, FiFileText,
  FiMessageSquare, FiPrinter,
  FiBriefcase, FiExternalLink, FiUserCheck, FiUsers
} from 'react-icons/fi';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../../lib/utils';

const getApiUrl = () => { if (typeof window !== 'undefined') { const hostname = window.location.hostname; if (hostname.includes('justasmile-events.com') || hostname.includes('app.justasmile')) { return ''; } } return process.env.REACT_APP_BACKEND_URL || ''; }; const API_URL = getApiUrl();

// Collaborator Assignment Component (only for admins)
const CollaboratorAssignment = ({ reservationId, assignedCollaborators = [], onUpdate }) => {
  const [collaborators, setCollaborators] = useState([]);
  const [selected, setSelected] = useState(assignedCollaborators || []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCollaborators();
  }, []);

  useEffect(() => {
    setSelected(assignedCollaborators || []);
  }, [assignedCollaborators]);

  const loadCollaborators = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/collaborators`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCollaborators(data);
      }
    } catch (error) {
      console.error('Error loading collaborators:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCollaborator = (collabId) => {
    setSelected(prev => 
      prev.includes(collabId) 
        ? prev.filter(id => id !== collabId)
        : [...prev, collabId]
    );
  };

  const saveAssignments = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}/assign-collaborators`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ collaborator_ids: selected })
      });
      
      if (response.ok) {
        toast.success('Collaborateurs assignés');
        if (onUpdate) onUpdate();
      } else {
        toast.error('Erreur lors de l\'assignation');
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500">Chargement...</div>;
  }

  if (collaborators.length === 0) {
    return (
      <div className="text-sm text-slate-500">
        <p>Aucun collaborateur créé.</p>
        <a href="/dashboard/collaborators" className="text-indigo-600 hover:underline">
          Créer un collaborateur
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {collaborators.map(collab => (
          <button
            key={collab.id}
            onClick={() => toggleCollaborator(collab.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
              selected.includes(collab.id)
                ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-400'
                : 'bg-slate-100 text-slate-600 border-2 border-transparent hover:bg-slate-200'
            }`}
          >
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              selected.includes(collab.id) ? 'bg-indigo-500 text-white' : 'bg-slate-300 text-slate-700'
            }`}>
              {collab.name?.charAt(0).toUpperCase()}
            </div>
            {collab.name}
            {selected.includes(collab.id) && <FiUserCheck className="text-indigo-600" />}
          </button>
        ))}
      </div>
      
      {JSON.stringify(selected) !== JSON.stringify(assignedCollaborators || []) && (
        <Button 
          onClick={saveAssignments} 
          disabled={saving}
          size="sm"
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer les assignations'}
        </Button>
      )}
    </div>
  );
};

const ReservationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isCollaborator } = useAuth();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const loadReservation = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reservations/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReservation(data);
      } else {
        toast.error('Réservation non trouvée');
        navigate('/dashboard/reservations');
      }
    } catch (error) {
      console.error('Error loading reservation:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReservation();
  }, [id]);

  const handleStatusChange = async (reservationId, newStatus, note = '') => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus, note })
      });
      
      if (response.ok) {
        await loadReservation();
      } else {
        throw new Error('Failed to update status');
      }
    } finally {
      setUpdating(false);
    }
  };

  const handlePaymentStatusChange = async (reservationId, paymentStatus) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}/payment`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ payment_status: paymentStatus })
      });
      
      if (response.ok) {
        await loadReservation();
      } else {
        throw new Error('Failed to update payment status');
      }
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Réservation non trouvée</p>
        <Button variant="outline" onClick={() => navigate('/dashboard/reservations')} className="mt-4">
          Retour aux réservations
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reservation-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard/reservations')}
            className="text-slate-600"
          >
            <FiArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {reservation.product_name}
            </h1>
            <p className="text-slate-500 text-sm">
              Réservation #{reservation.id?.slice(0, 8)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
          >
            <FiPrinter className="h-4 w-4 mr-2" />
            Imprimer
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FiUser className="h-4 w-4 text-slate-500" />
                Informations Client
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <FiUser className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{reservation.client_name}</p>
                    {reservation.client_type === 'entreprise' && (
                      <Badge className="bg-blue-100 text-blue-800 text-xs mt-1">
                        <FiBriefcase className="h-3 w-3 mr-1" />
                        {reservation.company_name || 'Entreprise'}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <FiMail className="h-4 w-4 text-slate-400" />
                    <a href={`mailto:${reservation.client_email}`} className="text-blue-600 hover:underline">
                      {reservation.client_email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FiPhone className="h-4 w-4 text-slate-400" />
                    <a href={`tel:${reservation.client_phone}`} className="text-blue-600 hover:underline">
                      {reservation.client_phone}
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Event Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FiCalendar className="h-4 w-4 text-slate-500" />
                Détails de l'Événement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wide">Date de début</label>
                    <p className="font-medium text-slate-900">{formatDate(reservation.start_date)}</p>
                  </div>
                  {reservation.event_time && (
                    <div>
                      <label className="text-xs text-slate-500 uppercase tracking-wide">Heure de début</label>
                      <p className="font-medium text-slate-900">{reservation.event_time}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wide">Date de fin</label>
                    <p className="font-medium text-slate-900">{formatDate(reservation.end_date)}</p>
                  </div>
                  {reservation.duration_hours && (
                    <div>
                      <label className="text-xs text-slate-500 uppercase tracking-wide">Durée</label>
                      <p className="font-medium text-slate-900">{reservation.duration_hours}h</p>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wide">Adresse</label>
                    <p className="font-medium text-slate-900 flex items-start gap-2">
                      <FiMapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      {reservation.event_address}
                    </p>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(reservation.event_address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1"
                    >
                      <FiExternalLink className="h-3 w-3" />
                      Voir sur Google Maps
                    </a>
                  </div>
                  {reservation.event_type && (
                    <div>
                      <label className="text-xs text-slate-500 uppercase tracking-wide">Type d'événement</label>
                      <p className="font-medium text-slate-900 capitalize">{reservation.event_type}</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Options Selected */}
          {reservation.options && reservation.options.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FiPackage className="h-4 w-4 text-slate-500" />
                  Options sélectionnées
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {reservation.options.map((option, idx) => (
                    <Badge key={idx} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      {typeof option === 'string' ? option : option.name || option.id}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Product Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FiPackage className="h-4 w-4 text-slate-500" />
                Prestation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-4">
                {reservation.product_image && (
                  <img
                    src={reservation.product_image}
                    alt={reservation.product_name}
                    className="w-24 h-24 rounded-lg object-cover"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">{reservation.product_name}</h3>
                  {reservation.variation_name && (
                    <p className="text-slate-600">{reservation.variation_name}</p>
                  )}
                  <div className="mt-3 grid gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Prix de base</span>
                      <span className="font-medium">{formatCurrency(reservation.rental_cost || reservation.total_price)}</span>
                    </div>
                    {reservation.km_cost > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Frais kilométriques ({reservation.distance_km} km)</span>
                        <span className="font-medium">{formatCurrency(reservation.km_cost)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-slate-200">
                      <span className="font-semibold text-slate-900">Total</span>
                      <span className="font-bold text-lg text-slate-900">{formatCurrency(reservation.total_price)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {reservation.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FiMessageSquare className="h-4 w-4 text-slate-500" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap">{reservation.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Status Manager */}
        <div className="space-y-6">
          <StatusManager
            reservation={reservation}
            onStatusChange={handleStatusChange}
            onPaymentStatusChange={handlePaymentStatusChange}
            isUpdating={updating}
          />

          {/* Collaborator Assignment - Only for admins, not for collaborators */}
          {!isCollaborator && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FiUsers className="h-4 w-4 text-indigo-500" />
                  Collaborateurs assignés
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CollaboratorAssignment
                  reservationId={reservation.id}
                  assignedCollaborators={reservation.assigned_collaborators}
                  onUpdate={loadReservation}
                />
              </CardContent>
            </Card>
          )}

          {/* Design Manager - Integrated validation */}
          <DesignManager
            reservationId={id}
            reservation={reservation}
            onUpdate={loadReservation}
          />

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Actions Rapides</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => window.open(`mailto:${reservation.client_email}?subject=Réservation ${reservation.product_name}`)}
              >
                <FiMail className="h-4 w-4 mr-2" />
                Envoyer un Email
              </Button>
              {reservation.deposit_invoice_id && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate(`/dashboard/factures`)}
                >
                  <FiFileText className="h-4 w-4 mr-2" />
                  Voir la Facture
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Photo Gallery Manager */}
          <PhotoGalleryManager
            reservationId={id}
            reservation={reservation}
            onUpdate={loadReservation}
          />

          {/* Meta Info */}
          <Card className="bg-slate-50">
            <CardContent className="pt-4">
              <div className="space-y-2 text-xs text-slate-500">
                <div className="flex justify-between">
                  <span>Créée le</span>
                  <span>{formatDate(reservation.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Modifiée le</span>
                  <span>{formatDate(reservation.updated_at)}</span>
                </div>
                {reservation.source && (
                  <div className="flex justify-between">
                    <span>Source</span>
                    <Badge variant="outline" className="text-xs">
                      {reservation.source === 'admin' ? 'Admin' : reservation.source === 'website' ? 'Site Web' : reservation.source}
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReservationDetailPage;
